import { API_ENDPOINTS } from './api/config';
import { PrimeArtifact } from '../types/primeArtifact';
import { PrimeArtifactStateCreate } from '../types/primeArtifactState';
import { PrimeArtifactStateTransitionCreate } from '../types/primeArtifactStateTransition';
import { PrimeProcessCreate } from '../types/primeProcess';
import { GPTResponse } from '../types/gptResponse';
import { apiClient } from './apiClient';

export class GPTService {
  private static instance: GPTService;

  static getInstance(): GPTService {
    if (!GPTService.instance) {
      GPTService.instance = new GPTService();
    }
    return GPTService.instance;
  }

  async query(prompt: string): Promise<GPTResponse> {
    const response = await apiClient.post<GPTResponse>(API_ENDPOINTS.GPT_INTEGRATION.QUERY, {
      prompt,
    });
    return response.data;
  }

  async generateArtifact(prompt: string): Promise<PrimeArtifact> {
    const response = await apiClient.post<PrimeArtifact>(
      API_ENDPOINTS.GPT_INTEGRATION.GENERATE_ARTIFACT,
      { prompt }
    );
    return response.data;
  }

  async generateStates(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeArtifactStateCreate[]> {
    const response = await apiClient.post<PrimeArtifactStateCreate[]>(
      `${API_ENDPOINTS.GPT_INTEGRATION.GENERATE_STATES}?prime_artifact_id=${prime_artifact_id}`,
      {
        name: 'Generated State',
        description: prompt,
        prime_artifact_ref: prime_artifact_id,
        is_required: true,
        state_type: 'PROCESSITEM',
        is_initial: false,
        is_final: false,
        is_improvement: false,
        demand_type: 'TARGET',
        demand_flow_type: 'DRUM',
        state_metadata: {},
        state_rules: {},
        state_parameters: {},
        validation_rules: {},
      }
    );
    return response.data;
  }

  async generateTransitions(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeArtifactStateTransitionCreate[]> {
    const response = await apiClient.post<PrimeArtifactStateTransitionCreate[]>(
      API_ENDPOINTS.GPT_INTEGRATION.GENERATE_TRANSITIONS,
      { prime_artifact_id, prompt }
    );
    return response.data;
  }

  async generateProcesses(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeProcessCreate[]> {
    const response = await apiClient.post<PrimeProcessCreate[]>(
      `${API_ENDPOINTS.GPT_INTEGRATION.GENERATE_PROCESSES}?artifact_id=${prime_artifact_id}`,
      {
        name: prompt,
        description: prompt,
        prime_artifact_ref: prime_artifact_id,
        process_type: 'DEMAND',
        demand_flow_type: 'DRUM',
        process_parameters: {},
        demand_calculation_rules: {},
        validation_rules: {},
        improvement_ref: null,
        improvement_parameters: {},
      }
    );
    return response.data;
  }

  async generateCompleteModel(prompt: string): Promise<unknown> {
    const response = await apiClient.post<unknown>(
      API_ENDPOINTS.GPT_INTEGRATION.GENERATE_COMPLETE_MODEL,
      { prompt }
    );
    return response.data;
  }

  async generateFollowup(prime_artifact_id: string, prompt: string): Promise<GPTResponse> {
    const response = await apiClient.post<GPTResponse>(
      API_ENDPOINTS.GPT_INTEGRATION.GENERATE_FOLLOWUP,
      { prime_artifact_id, prompt }
    );
    return response.data;
  }

  async processResponse(responseId: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.GPT_INTEGRATION.PROCESS_RESPONSE, {
      response_id: responseId,
    });
  }

  async checkDuplicates(prime_artifact_id: string): Promise<{ duplicates: boolean }> {
    const response = await apiClient.post<{ duplicates: boolean }>(
      API_ENDPOINTS.GPT_INTEGRATION.CHECK_DUPLICATES,
      { prime_artifact_id }
    );
    return response.data;
  }

  async getResponses(prime_artifact_id: string): Promise<GPTResponse[]> {
    console.log(
      '[GPTService] Fetching responses with URL:',
      `/gpt/?artifact_id=${prime_artifact_id}`
    );
    const response = await apiClient.get<GPTResponse[]>(`/gpt/?artifact_id=${prime_artifact_id}`);
    console.log('[GPTService] Response:', response);
    return response.data;
  }

  async getResponse(responseId: string): Promise<GPTResponse> {
    const response = await apiClient.get<GPTResponse>(`/gpt-responses/${responseId}`);
    return response.data;
  }

  async approveResponse(responseId: string): Promise<void> {
    await apiClient.post(`/gpt-responses/${responseId}/approve`);
  }

  // Static methods for backward compatibility
  static async generateArtifact(prompt: string): Promise<PrimeArtifact> {
    return this.getInstance().generateArtifact(prompt);
  }

  static async generateStates(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeArtifactStateCreate[]> {
    return this.getInstance().generateStates(prime_artifact_id, prompt);
  }

  static async generateTransitions(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeArtifactStateTransitionCreate[]> {
    return this.getInstance().generateTransitions(prime_artifact_id, prompt);
  }

  static async generateProcesses(
    prime_artifact_id: string,
    prompt: string
  ): Promise<PrimeProcessCreate[]> {
    return this.getInstance().generateProcesses(prime_artifact_id, prompt);
  }

  static async generateCompleteModel(prompt: string): Promise<unknown> {
    return this.getInstance().generateCompleteModel(prompt);
  }

  static async generateFollowup(prime_artifact_id: string, prompt: string): Promise<GPTResponse> {
    return this.getInstance().generateFollowup(prime_artifact_id, prompt);
  }

  static async processResponse(responseId: string): Promise<void> {
    return this.getInstance().processResponse(responseId);
  }

  static async checkDuplicates(prime_artifact_id: string): Promise<{ duplicates: boolean }> {
    return this.getInstance().checkDuplicates(prime_artifact_id);
  }

  static async getResponses(prime_artifact_id: string): Promise<GPTResponse[]> {
    return this.getInstance().getResponses(prime_artifact_id);
  }

  static async getResponse(responseId: string): Promise<GPTResponse> {
    return this.getInstance().getResponse(responseId);
  }

  static async approveResponse(responseId: string): Promise<void> {
    return this.getInstance().approveResponse(responseId);
  }
}
